import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X } from "lucide-react";
import { SolveAssistantChat } from "./SolveAssistantChat";
import solveLogoNew from "@/assets/solve-iagro-logo-new.png";

interface SolveFloatingAssistantProps {
  currentPage?: string;
}

export function SolveFloatingAssistant({ currentPage = "/" }: SolveFloatingAssistantProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showBadge, setShowBadge] = useState(false);

  // Show notification badge after 10 seconds
  useState(() => {
    const timer = setTimeout(() => {
      setShowBadge(true);
    }, 10000);
    return () => clearTimeout(timer);
  });

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setShowBadge(false); // Hide badge when chat is opened
    }
  };

  return (
    <>
      {/* Floating Button */}
      <motion.div
        className="fixed bottom-6 right-6 z-50"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
      >
        <motion.button
          onClick={toggleChat}
          className="relative bg-primary hover:bg-primary/90 text-primary-foreground rounded-full p-4 shadow-2xl transition-all duration-300"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          animate={{
            boxShadow: [
              "0 0 0 0 rgba(34, 197, 94, 0.4)",
              "0 0 0 15px rgba(34, 197, 94, 0)",
              "0 0 0 0 rgba(34, 197, 94, 0)"
            ]
          }}
          transition={{
            boxShadow: {
              repeat: Infinity,
              duration: 2,
              ease: "easeInOut"
            }
          }}
        >
          {isOpen ? (
            <X className="h-7 w-7" />
          ) : (
            <div className="relative">
              <img 
                src={solveLogoNew} 
                alt="Solve" 
                className="h-8 w-8 object-contain"
              />
              {/* Notification Badge */}
              <AnimatePresence>
                {showBadge && !isOpen && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full h-3 w-3"
                  />
                )}
              </AnimatePresence>
            </div>
          )}
        </motion.button>

        {/* Help Badge Tooltip */}
        <AnimatePresence>
          {showBadge && !isOpen && (
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="absolute bottom-0 right-20 bg-card border border-border rounded-lg px-3 py-2 shadow-lg whitespace-nowrap"
            >
              <p className="text-sm font-medium">Precisa de ajuda? 🌾</p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Chat Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed bottom-24 right-6 z-50"
          >
            <SolveAssistantChat 
              currentPage={currentPage} 
              onClose={() => setIsOpen(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
